import { useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'This reset link is invalid or has expired. Request a new one.');
      setDone(true);
      setTimeout(() => { window.location.href = '/login'; }, 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png" alt="Keder Screens" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Reset Password</h1>
        </div>
        <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-6">
          {!token ? (
            <div className="text-center space-y-4">
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
                Missing reset token. Open the link from your email again, or request a new one.
              </div>
              <a href="/login" className="text-sm text-[#FFCD24] hover:underline">← Back to sign in</a>
            </div>
          ) : done ? (
            <div className="text-center space-y-3">
              <div className="bg-green-900/40 border border-green-600 text-green-200 px-4 py-3 rounded-lg text-sm">
                Password updated. Redirecting you to sign in…
              </div>
              <a href="/login" className="text-sm text-[#FFCD24] hover:underline">Go to sign in now</a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">{error}</div>}
              <div>
                <label className="block text-[#FFCD24] font-medium mb-1">New password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-lg bg-[#111] border border-[#333] text-white placeholder-gray-500 focus:border-[#FFCD24] focus:outline-none focus:ring-2 focus:ring-[#FFCD24]/30"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="block text-[#FFCD24] font-medium mb-1">Confirm password</label>
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-lg bg-[#111] border border-[#333] text-white placeholder-gray-500 focus:border-[#FFCD24] focus:outline-none focus:ring-2 focus:ring-[#FFCD24]/30"
                  placeholder="Re-enter password"
                />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-[#FFCD24] hover:bg-yellow-400 text-black font-semibold py-3 rounded-lg transition-colors disabled:opacity-50">
                {loading ? 'Updating…' : 'Update password'}
              </button>
              <div className="text-center">
                <a href="/login" className="text-sm text-[#FFCD24] hover:underline">← Back to sign in</a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
